import React, { useState } from 'react';
import { X, Copy, CheckCircle, ExternalLink, Mail, Key, Shield, AlertCircle } from 'lucide-react';

const EmailAppPasswordHelp = ({ isOpen, onClose }) => {
  const [activeProvider, setActiveProvider] = useState('gmail');
  const [copiedStep, setCopiedStep] = useState(null);

  const copyToClipboard = (text, stepId) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(stepId);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const providers = {
    gmail: {
      name: 'Gmail',
      color: '#EA4335',
      icon: Mail,
      videoUrl: 'https://support.google.com/accounts/answer/185833',
      steps: [
        {
          id: 1,
          title: 'Enable 2-Factor Authentication',
          description: 'App passwords require 2FA to be enabled on your Google account.',
          details: [
            'Go to your Google Account settings',
            'Navigate to Security → 2-Step Verification',
            'Follow the prompts to enable 2FA'
          ],
          link: 'https://myaccount.google.com/security'
        },
        {
          id: 2,
          title: 'Generate App Password',
          description: 'Create a unique password specifically for CyberGuard.',
          details: [
            'Go to Google Account → Security',
            'Under "2-Step Verification", click "App passwords"',
            'Select app: "Mail"',
            'Select device: "Other (Custom name)"',
            'Enter: "CyberGuard Email Protection"',
            'Click "Generate"'
          ],
          link: 'https://myaccount.google.com/apppasswords',
          copyable: true
        },
        {
          id: 3,
          title: 'Copy & Use the Password',
          description: 'Google will display a 16-character password.',
          details: [
            'Copy the password (spaces don\'t matter)',
            'Paste it into the CyberGuard password field',
            'Click "Connect" to enable protection'
          ],
          note: 'Important: You can only see this password once! If you lose it, generate a new one.'
        }
      ],
      troubleshooting: [
        {
          issue: '"App passwords" option not available',
          solution: 'Make sure 2-Factor Authentication is enabled first. If you use Advanced Protection, app passwords are not available.'
        },
        {
          issue: 'Connection fails with correct password',
          solution: 'Try removing spaces from the app password, or generate a new one.'
        }
      ]
    },
    outlook: {
      name: 'Outlook / Microsoft 365',
      color: '#0078D4',
      icon: Mail,
      videoUrl: 'https://support.microsoft.com/en-us/account-billing/manage-app-passwords-for-two-step-verification-d6dc8c6d-4bf7-4851-ad95-6d07799387e9',
      steps: [
        {
          id: 1,
          title: 'Enable Two-Step Verification',
          description: 'App passwords require two-step verification on your Microsoft account.',
          details: [
            'Sign in to Microsoft Account Security',
            'Select "Advanced security options"',
            'Under "Additional security", turn on "Two-step verification"'
          ],
          link: 'https://account.microsoft.com/security'
        },
        {
          id: 2,
          title: 'Create App Password',
          description: 'Generate a password for CyberGuard.',
          details: [
            'Go to Security basics → More security options',
            'Scroll to "App passwords"',
            'Click "Create a new app password"',
            'A password will be generated automatically'
          ],
          link: 'https://account.live.com/proofs/AppPassword'
        },
        {
          id: 3,
          title: 'Save and Connect',
          description: 'Copy the generated password immediately.',
          details: [
            'Copy the displayed password',
            'You won\'t be able to see it again',
            'Paste into CyberGuard and connect'
          ],
          note: 'Microsoft app passwords are permanent until you delete them.'
        }
      ],
      troubleshooting: [
        {
          issue: 'Two-step verification unavailable',
          solution: 'Your organization might have disabled this feature. Contact your IT admin.'
        },
        {
          issue: 'Too many app passwords created',
          solution: 'Delete unused app passwords from your Microsoft security settings.'
        }
      ]
    },
    yahoo: {
      name: 'Yahoo Mail',
      color: '#6001D2',
      icon: Mail,
      videoUrl: 'https://help.yahoo.com/kb/generate-manage-third-party-passwords-sln15241.html',
      steps: [
        {
          id: 1,
          title: 'Go to Account Security',
          description: 'Access your Yahoo account security settings.',
          details: [
            'Sign in to Yahoo Mail',
            'Click your profile icon (top right)',
            'Select "Account Info"',
            'Click "Account Security" in the left menu'
          ],
          link: 'https://login.yahoo.com/account/security'
        },
        {
          id: 2,
          title: 'Generate App Password',
          description: 'Create a password for third-party apps.',
          details: [
            'Click "Generate app password"',
            'Select "Other App" from the dropdown',
            'Enter "CyberGuard" as the app name',
            'Click "Generate"'
          ]
        },
        {
          id: 3,
          title: 'Use the Password',
          description: 'Copy and paste into CyberGuard.',
          details: [
            'Copy the 16-character password',
            'Paste into CyberGuard password field',
            'Click "Connect" to start protection'
          ],
          note: 'Yahoo shows the password only once. Save it securely or generate a new one if lost.'
        }
      ],
      troubleshooting: [
        {
          issue: 'Cannot generate app password',
          solution: 'Make sure you\'re using Yahoo Mail (not AT&T or other variants). Try signing out and back in.'
        }
      ]
    },
    imap: {
      name: 'IMAP / Other Providers',
      color: '#64748b',
      icon: Mail,
      steps: [
        {
          id: 1,
          title: 'Check Provider Requirements',
          description: 'Different email providers have different security requirements.',
          details: [
            'ProtonMail: Use Bridge application',
            'iCloud: Generate app-specific password',
            'FastMail: Use regular password or app password',
            'Custom servers: Check with your email administrator'
          ]
        },
        {
          id: 2,
          title: 'Gather IMAP Settings',
          description: 'You\'ll need specific server information.',
          details: [
            'IMAP server address (e.g., imap.example.com)',
            'Port number (usually 993 for SSL)',
            'Your full email address',
            'App password or account password'
          ]
        },
        {
          id: 3,
          title: 'Common IMAP Servers',
          description: 'Reference for popular providers:',
          details: [
            'iCloud: imap.mail.me.com (port 993)',
            'ProtonMail: 127.0.0.1 (requires Bridge)',
            'Zoho: imap.zoho.com (port 993)',
            'FastMail: imap.fastmail.com (port 993)'
          ],
          copyable: true
        }
      ],
      troubleshooting: [
        {
          issue: 'Connection timeout',
          solution: 'Verify the IMAP server address and port. Check if your firewall allows IMAP connections.'
        },
        {
          issue: 'Authentication failed',
          solution: 'Some providers require "less secure app access" or app-specific passwords. Check your provider\'s documentation.'
        }
      ]
    }
  };

  if (!isOpen) return null;

  const currentProvider = providers[activeProvider];
  const ProviderIcon = currentProvider.icon;

  return (
    <div
      className="fixed inset-0 z-[60]"
      style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: isOpen ? 1 : 0 }}
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div
        className="absolute top-0 right-0 h-full w-full md:w-[500px] transition-transform duration-300 ease-out overflow-hidden"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderLeft: '1px solid rgba(0, 212, 255, 0.2)',
          boxShadow: '-10px 0 50px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 p-6 border-b"
          style={{
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div
                className="p-2 rounded-lg"
                style={{ background: 'rgba(0, 212, 255, 0.2)' }}
              >
                <Key className="h-6 w-6" style={{ color: '#00d4ff' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: '#ffffff' }}>
                  App Password Setup
                </h2>
                <p className="text-sm" style={{ color: '#94a3b8' }}>
                  Step-by-step guide
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all hover:scale-110"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#94a3b8'
              }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Provider Tabs */}
          <div className="flex space-x-2 overflow-x-auto">
            {Object.entries(providers).map(([key, provider]) => (
              <button
                key={key}
                onClick={() => setActiveProvider(key)}
                className="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all"
                style={{
                  background: activeProvider === key
                    ? 'rgba(0, 212, 255, 0.2)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: activeProvider === key
                    ? '1px solid rgba(0, 212, 255, 0.5)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  color: activeProvider === key ? '#00d4ff' : '#94a3b8'
                }}
              >
                {provider.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-140px)] p-6">
          {/* Video/Documentation Link */}
          {currentProvider.videoUrl && (
            <a
              href={currentProvider.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-6 p-4 rounded-xl transition-all hover:scale-105"
              style={{
                background: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ background: 'rgba(139, 92, 246, 0.2)' }}
                  >
                    <ExternalLink className="h-5 w-5" style={{ color: '#8b5cf6' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm" style={{ color: '#ffffff' }}>
                      Official Documentation
                    </h3>
                    <p className="text-xs" style={{ color: '#94a3b8' }}>
                      View {currentProvider.name}'s guide
                    </p>
                  </div>
                </div>
                <ExternalLink className="h-4 w-4" style={{ color: '#8b5cf6' }} />
              </div>
            </a>
          )}

          {/* Steps */}
          <div className="space-y-6">
            {currentProvider.steps.map((step, index) => (
              <div
                key={step.id}
                className="p-5 rounded-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {/* Step Header */}
                <div className="flex items-start space-x-3 mb-3">
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                    style={{
                      background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                      color: '#ffffff'
                    }}
                  >
                    {step.id}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1" style={{ color: '#ffffff' }}>
                      {step.title}
                    </h3>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Step Details */}
                <ul className="space-y-2 ml-11 mb-3">
                  {step.details.map((detail, idx) => (
                    <li
                      key={idx}
                      className="flex items-start space-x-2 text-sm"
                      style={{ color: '#e2e8f0' }}
                    >
                      <span className="text-cyan-400 mt-1">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>

                {/* Direct Link */}
                {step.link && (
                  <a
                    href={step.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-11 inline-flex items-center space-x-2 text-sm font-medium transition-all hover:scale-105"
                    style={{ color: '#00d4ff' }}
                  >
                    <span>Open Settings Page</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}

                {/* Important Note */}
                {step.note && (
                  <div
                    className="ml-11 mt-3 p-3 rounded-lg flex items-start space-x-2"
                    style={{
                      background: 'rgba(255, 165, 0, 0.1)',
                      border: '1px solid rgba(255, 165, 0, 0.3)'
                    }}
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#ffa500' }} />
                    <p className="text-xs" style={{ color: '#ffa500' }}>
                      {step.note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Troubleshooting */}
          {currentProvider.troubleshooting && currentProvider.troubleshooting.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2" style={{ color: '#ffffff' }}>
                <Shield className="h-5 w-5" style={{ color: '#00d4ff' }} />
                <span>Troubleshooting</span>
              </h3>
              <div className="space-y-3">
                {currentProvider.troubleshooting.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-lg"
                    style={{
                      background: 'rgba(255, 107, 53, 0.1)',
                      border: '1px solid rgba(255, 107, 53, 0.3)'
                    }}
                  >
                    <h4 className="font-semibold text-sm mb-2" style={{ color: '#ff6b35' }}>
                      Problem: {item.issue}
                    </h4>
                    <p className="text-sm" style={{ color: '#e2e8f0' }}>
                      <strong style={{ color: '#00ff88' }}>Solution:</strong> {item.solution}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Security Note */}
          <div
            className="mt-8 p-4 rounded-xl"
            style={{
              background: 'rgba(0, 255, 136, 0.1)',
              border: '1px solid rgba(0, 255, 136, 0.3)'
            }}
          >
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#00ff88' }} />
              <div>
                <h4 className="font-semibold text-sm mb-1" style={{ color: '#00ff88' }}>
                  Your Security is Our Priority
                </h4>
                <p className="text-xs" style={{ color: '#e2e8f0' }}>
                  App passwords are safer than using your main password. CyberGuard never stores your password
                  in plain text and uses enterprise-grade encryption for all communications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailAppPasswordHelp;
